import { createContext, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';

import styles from '@context/PopoverStore.module.css';
import { PopoverContextType, PopoverState } from '@typing/State';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const popoverContext = createContext({} as PopoverContextType);

const initialState: PopoverState = {
  bottomMost: false,
  content: undefined,
  isOpen: false,
  rightMost: false,
  xPosition: 0,
  yPosition: 0
};

const PopoverStore = ({ children }: { children: ReactNode }): ReactElement => {
  const [popoverState, setPopoverState] = useState(initialState);

  const popoverPositionCSS = useMemo(() => {
    const styles = {
      bottom: '',
      left: '',
      right: '',
      top: ''
    };
    if (popoverState.bottomMost) {
      styles.bottom = `${popoverState.yPosition}px`;
    } else {
      styles.top = `${popoverState.yPosition}px`;
    }
    if (popoverState.rightMost) {
      styles.right = `${popoverState.xPosition}px`;
    } else {
      styles.left = `${popoverState.xPosition}px`;
    }
    return styles;
  }, [popoverState.bottomMost, popoverState.rightMost, popoverState.xPosition, popoverState.yPosition]);

  const showPopover = useCallback((content: React.ReactNode, event: React.MouseEvent) => {
    const rightMost = event.nativeEvent.pageX > window.innerWidth / 2;
    const bottomMost = event.nativeEvent.pageY > window.innerHeight / 2;

    setPopoverState(_prevState => ({
      bottomMost,
      content,
      isOpen: true,
      rightMost,
      xPosition: rightMost ? window.innerWidth - (event.nativeEvent.pageX - 25) : event.nativeEvent.pageX + 25,
      yPosition: bottomMost ? window.innerHeight - event.nativeEvent.pageY : event.nativeEvent.pageY
    }));
  }, []);

  const hidePopover = useCallback(() => {
    setPopoverState(_prevState => ({
      bottomMost: false,
      content: undefined,
      isOpen: false,
      leftMost: false,
      rightMost: false,
      xPosition: 0,
      yPosition: 0
    }));
  }, []);

  return (
    <popoverContext.Provider value={{ hidePopover, popoverState, showPopover }}>
      {popoverState.isOpen && (
        <div className={styles.hoverText} onClick={hidePopover} style={popoverPositionCSS}>
          {popoverState.content}
        </div>
      )}
      {children}
    </popoverContext.Provider>
  );
};

export default PopoverStore;
